import { cloneElement, isValidElement, useId } from 'react';
import type { ReactElement, ReactNode } from 'react';

type AddressSearchResult = {
  place_id?: string;
  formatted_address?: string;
  lat?: number | null;
  long?: number | null;
};

type AddressFieldKey =
  | 'rua'
  | 'numero'
  | 'bairro'
  | 'cep'
  | 'cidade'
  | 'estado'
  | 'endereco_lat'
  | 'endereco_long';

type AddressEditorValues = Partial<Record<AddressFieldKey, string>>;

type NativeControlProps = {
  id?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
};

interface AddressEditorProps {
  values: AddressEditorValues;
  errors?: Partial<Record<AddressFieldKey, string>>;
  dirtyFields?: Partial<Record<AddressFieldKey, boolean>>;
  inputClassName: string;
  errorFieldClassName?: string;
  errorInputClassName?: string;
  searchValue?: string;
  searching?: boolean;
  searchResults?: AddressSearchResult[];
  onSearchValueChange?: (value: string) => void;
  onSearch?: () => void;
  onSelectSearchResult?: (result: AddressSearchResult) => void;
  onFieldChange: (field: AddressFieldKey, value: string) => void;
  showSearch?: boolean;
  showCityState?: boolean;
  showStreetFields?: boolean;
  showGeoFields?: boolean;
  geoFieldsInAdvanced?: boolean;
  cityStateRequired?: boolean;
  streetRequired?: boolean;
  neighborhoodRequired?: boolean;
  postalCodeRequired?: boolean;
  ufOptions?: string[];
  cityOptions?: string[];
  cityListId?: string;
  searchLabel?: string;
  searchHelper?: string;
  searchPlaceholder?: string;
}

const LabelHint = ({ text }: { text: string }) => (
  <span
    className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 bg-white text-[10px] font-bold text-slate-500"
    title={text}
    aria-label={text}
  >
    i
  </span>
);

const FieldBlock = ({
  label,
  required,
  helper,
  error,
  dirty,
  tooltip,
  errorKey,
  errorFieldClassName,
  children,
}: {
  label: string;
  required?: boolean;
  helper?: string;
  error?: string;
  dirty?: boolean;
  tooltip?: string;
  errorKey?: string;
  errorFieldClassName?: string;
  children: ReactNode;
}) => {
  const generatedId = useId().replace(/:/g, '');
  const controlId = `address-field-${generatedId}`;
  const helperId = `${controlId}-help`;
  const errorId = `${controlId}-error`;
  const canAssociate = isValidElement(children) && typeof children.type === 'string' && ['input', 'select', 'textarea'].includes(children.type);
  const control = canAssociate ? (children as ReactElement<NativeControlProps>) : null;
  const renderedChildren = control
    ? cloneElement(control, {
        id: control.props.id || controlId,
        'aria-invalid': Boolean(error),
        'aria-describedby': error ? errorId : (helper ? helperId : undefined),
      })
    : children;

  return <div
    className={`form-prompt-field space-y-1.5${error ? ` is-error ${errorFieldClassName || ''}` : ''}${dirty ? ' is-draft' : ''}`}
    data-error-key={errorKey}
  >
    <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-700">
      <label htmlFor={canAssociate ? controlId : undefined}>{label}</label>
      {tooltip ? <LabelHint text={tooltip} /> : null}
      {required ? <span className="text-rose-600" aria-label="Campo obrigatório">*</span> : null}
      {dirty ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
          Rascunho
        </span>
      ) : null}
    </div>
    {renderedChildren}
    {error ? (
      <p id={errorId} className="text-xs font-semibold text-rose-600">{error}</p>
    ) : helper ? (
      <p id={helperId} className="text-xs text-slate-500">{helper}</p>
    ) : null}
  </div>
};

const AddressEditor = ({
  values,
  errors = {},
  dirtyFields = {},
  inputClassName,
  errorFieldClassName = '',
  errorInputClassName = '',
  searchValue = '',
  searching = false,
  searchResults = [],
  onSearchValueChange,
  onSearch,
  onSelectSearchResult,
  onFieldChange,
  showSearch = true,
  showCityState = true,
  showStreetFields = true,
  showGeoFields = true,
  geoFieldsInAdvanced = false,
  cityStateRequired = false,
  streetRequired = false,
  neighborhoodRequired = false,
  postalCodeRequired = false,
  ufOptions = [],
  cityOptions = [],
  cityListId = 'address-editor-city-list',
  searchLabel = 'Buscar endereço',
  searchHelper = 'Digite um endereço. O primeiro resultado já preenche os campos automaticamente; você pode trocar clicando em outro resultado.',
  searchPlaceholder = 'Ex.: Av. Praia de Belas, Porto Alegre - RS',
}: AddressEditorProps) => {
  const inputClass = (hasError: boolean) =>
    `${inputClassName} form-prompt-control${hasError && errorInputClassName ? ` ${errorInputClassName}` : ''}`;

  return (
    <div className="space-y-4">
    {showSearch && onSearch && onSearchValueChange && onSelectSearchResult ? (
      <FieldBlock
        label={searchLabel}
        helper={searchHelper}
        tooltip="Use a busca para localizar o endereço principal do estabelecimento."
      >
        <div className="grid gap-2 md:grid-cols-[1fr_auto]">
          <input
            className={inputClass(false)}
            aria-label={searchLabel}
            value={searchValue}
            onChange={(event) => onSearchValueChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSearch();
              }
            }}
            placeholder={searchPlaceholder}
          />
          <button
            type="button"
            onClick={onSearch}
            disabled={searching}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-teal-400 hover:text-teal-700 disabled:opacity-60"
          >
            {searching ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {searchResults.length > 0 && (
          <div className="mt-2 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
            {searchResults.map((result, index) => (
              <button
                key={result.place_id || `${result.lat}-${result.long}-${index}`}
                type="button"
                onClick={() => onSelectSearchResult(result)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 hover:border-teal-400/60 hover:bg-slate-50"
              >
                <span className="block font-semibold">
                  {result.formatted_address || 'Endereço encontrado'}
                </span>
                <span className="block text-[11px] text-slate-400">Clique para usar este endereço.</span>
              </button>
            ))}
          </div>
        )}
      </FieldBlock>
    ) : null}

    {showCityState && (
      <div className="grid gap-4 md:grid-cols-2">
        <FieldBlock
          label="Estado"
          required={cityStateRequired}
          error={errors.estado}
          dirty={dirtyFields.estado}
          errorKey="estado"
          errorFieldClassName={errorFieldClassName}
          tooltip="Usado junto da cidade em respostas de localização."
        >
          {ufOptions.length ? (
            <select
              className={inputClass(Boolean(errors.estado))}
              value={values.estado || ''}
              onChange={(event) => {
                onFieldChange('estado', event.target.value);
                onFieldChange('cidade', '');
              }}
            >
              <option value="">Selecione</option>
              {ufOptions.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          ) : (
            <input
              className={inputClass(Boolean(errors.estado))}
              value={values.estado || ''}
              maxLength={2}
              onChange={(event) => onFieldChange('estado', event.target.value.toUpperCase())}
              placeholder="Ex.: RS"
            />
          )}
        </FieldBlock>

        {values.estado ? (
          <FieldBlock
            label="Cidade"
            required={cityStateRequired}
            error={errors.cidade}
            dirty={dirtyFields.cidade}
            errorKey="cidade"
            errorFieldClassName={errorFieldClassName}
            tooltip="Usado para respostas de localização e para eventos."
          >
            <input
              className={inputClass(Boolean(errors.cidade))}
              list={cityListId}
              value={values.cidade}
              onChange={(event) => onFieldChange('cidade', event.target.value)}
              placeholder="Ex.: Porto Alegre"
            />
            <datalist id={cityListId}>
              {cityOptions.map((city) => (
                <option key={city} value={city} />
              ))}
            </datalist>
          </FieldBlock>
        ) : (
          <FieldBlock
            label="Cidade"
            required={cityStateRequired}
            helper="Selecione o estado para ver as cidades."
          >
            <input
              className={inputClass(false)}
              disabled
              placeholder="Selecione o estado primeiro"
            />
          </FieldBlock>
        )}
      </div>
    )}

    {showStreetFields && (
      <div className="grid gap-4 md:grid-cols-[1.6fr_0.7fr_1fr_0.9fr]">
        <FieldBlock
          label="Rua / Avenida"
          required={streetRequired}
          error={errors.rua}
          dirty={dirtyFields.rua}
          errorKey="rua"
          errorFieldClassName={errorFieldClassName}
          tooltip="Endereço usado para retirada, reservas e dúvidas gerais."
        >
          <input
            className={inputClass(Boolean(errors.rua))}
            value={values.rua}
            onChange={(event) => onFieldChange('rua', event.target.value)}
            placeholder="Ex.: Av. Venâncio Aires"
          />
        </FieldBlock>

        <FieldBlock
          label="Número"
          error={errors.numero}
          dirty={dirtyFields.numero}
          errorKey="numero"
          errorFieldClassName={errorFieldClassName}
        >
          <input
            className={inputClass(Boolean(errors.numero))}
            value={values.numero}
            onChange={(event) => onFieldChange('numero', event.target.value)}
            placeholder="Ex.: 123"
          />
        </FieldBlock>

        <FieldBlock
          label={neighborhoodRequired ? 'Bairro' : 'Bairro (opcional)'}
          required={neighborhoodRequired}
          error={errors.bairro}
          dirty={dirtyFields.bairro}
          errorKey="bairro"
          errorFieldClassName={errorFieldClassName}
        >
          <input
            className={inputClass(Boolean(errors.bairro))}
            value={values.bairro}
            onChange={(event) => onFieldChange('bairro', event.target.value)}
            placeholder="Ex.: Praia de Belas"
          />
        </FieldBlock>

        <FieldBlock
          label={postalCodeRequired ? 'CEP' : 'CEP (opcional)'}
          required={postalCodeRequired}
          error={errors.cep}
          dirty={dirtyFields.cep}
          errorKey="cep"
          errorFieldClassName={errorFieldClassName}
          tooltip="Ajuda a completar o endereço."
        >
          <input
            className={inputClass(Boolean(errors.cep))}
            inputMode="numeric"
            maxLength={9}
            value={values.cep}
            onChange={(event) => onFieldChange('cep', event.target.value)}
            placeholder="Ex.: 90000-000"
          />
        </FieldBlock>
      </div>
    )}

    {showGeoFields && !geoFieldsInAdvanced && (
      <div className="grid gap-4 md:grid-cols-2">
        <FieldBlock
          label="Latitude (opcional)"
          error={errors.endereco_lat}
          dirty={dirtyFields.endereco_lat}
          errorKey="endereco_lat"
          errorFieldClassName={errorFieldClassName}
        >
          <input
            className={inputClass(Boolean(errors.endereco_lat))}
            value={values.endereco_lat}
            onChange={(event) => onFieldChange('endereco_lat', event.target.value)}
            placeholder="Latitude"
          />
        </FieldBlock>
        <FieldBlock
          label="Longitude (opcional)"
          error={errors.endereco_long}
          dirty={dirtyFields.endereco_long}
          errorKey="endereco_long"
          errorFieldClassName={errorFieldClassName}
        >
          <input
            className={inputClass(Boolean(errors.endereco_long))}
            value={values.endereco_long}
            onChange={(event) => onFieldChange('endereco_long', event.target.value)}
            placeholder="Longitude"
          />
        </FieldBlock>
      </div>
    )}
    {showGeoFields && geoFieldsInAdvanced && (
      <details className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <summary className="cursor-pointer text-sm font-semibold text-slate-700">
          Configurações avançadas
        </summary>
        <p className="mt-2 text-xs text-slate-500">
          Coordenadas usadas para confirmar a localização e calcular distâncias.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <FieldBlock
            label="Latitude"
            error={errors.endereco_lat}
            dirty={dirtyFields.endereco_lat}
            errorKey="endereco_lat"
            errorFieldClassName={errorFieldClassName}
          >
            <input
              className={inputClass(Boolean(errors.endereco_lat))}
              value={values.endereco_lat}
              onChange={(event) => onFieldChange('endereco_lat', event.target.value)}
              placeholder="Latitude"
            />
          </FieldBlock>
          <FieldBlock
            label="Longitude"
            error={errors.endereco_long}
            dirty={dirtyFields.endereco_long}
            errorKey="endereco_long"
            errorFieldClassName={errorFieldClassName}
          >
            <input
              className={inputClass(Boolean(errors.endereco_long))}
              value={values.endereco_long}
              onChange={(event) => onFieldChange('endereco_long', event.target.value)}
              placeholder="Longitude"
            />
          </FieldBlock>
        </div>
      </details>
    )}
    </div>
  );
};

export default AddressEditor;
